import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateRecord } from './api';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const recordSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  content: z.string().min(1, 'Content is required'),
  tags: z.string().optional(),
});

type RecordForm = z.infer<typeof recordSchema>;

interface NewRecordFormProps {
  onSuccess?: () => void;
}

export function NewRecordForm({ onSuccess }: NewRecordFormProps) {
  const createRecord = useCreateRecord();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RecordForm>({
    resolver: zodResolver(recordSchema),
  });

  const onSubmit = async (data: RecordForm) => {
    try {
      const tags = data.tags 
        ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];

      await createRecord.mutateAsync({
        title: data.title,
        description: data.description,
        content: data.content,
        tags,
      });

      reset();
      onSuccess?.();
    } catch (error) {
      console.error('Failed to create record:', error);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Plus" size={20} className="text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Create New Record</h3>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Title"
          placeholder="Enter record title"
          {...register('title')}
          error={errors.title?.message}
          required
        />

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Description
          </label>
          <textarea
            placeholder="Optional description"
            {...register('description')}
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Content <span className="text-destructive">*</span>
          </label>
          <textarea
            placeholder="Enter the content to be notarized"
            {...register('content')}
            className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          {errors.content && (
            <p className="text-sm text-destructive">{errors.content.message}</p>
          )}
        </div>

        <Input
          label="Tags"
          placeholder="tag1, tag2, tag3"
          {...register('tags')}
          description="Separate tags with commas"
        />

        {createRecord.error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">
              {createRecord.error.message}
            </p>
          </div>
        )}

        <div className="flex space-x-3">
          <Button
            type="submit"
            loading={createRecord.isPending}
            iconName="Shield"
            iconPosition="left"
            className="flex-1"
          >
            Create Record
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => reset()}
            disabled={createRecord.isPending}
          >
            Clear
          </Button>
        </div>
      </form>
    </div>
  );
}